import { Box, CircularProgress } from "@mui/material"
import { useNavigate, useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback, useEffect } from "react"

import { BuiltPalletsActionButtons } from "./BuiltPalletsTab/BuiltPalletsActionButtons"
import { BulkPutAwayModal } from "./BuiltPalletsTab/BulkPutAwayModal"
import { CheckedInBuiltPalletsTable } from "./BuiltPalletsTab/CheckedInBuiltPalletsTable"
import { usePutAwayFlow } from "./BuiltPalletsTab/hooks/usePutAwayFlow"
import { ManualPutAwayModal } from "./BuiltPalletsTab/ManualPutAwayModal"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { ErrorMessage } from "@/components/ErrorMessage/ErrorMessage"
import SearchInput from "@/components/SearchInput"
import { PermissionActionType } from "@/constants/permissions"
import { GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { BasicPathType, useAppView } from "@/hooks/useAppView"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { useUpdateGoodsReceiptPallet } from "@/screens/Tablet/hooks/useUpdateGoodsReceiptPallet"
import type { DestinationOverride } from "@/screens/WarehouseReceiving/components"
import { GoodsReceiptPalletDeleteConfirmation } from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletDeleteConfirmation"
import { GoodsReceiptPalletLpnFormDialog } from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletLpnFormDialog"
import { GoodsReceiptPalletDestinationOverrideDialog } from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletsTable/cells/GoodsReceiptPalletDestinationOverrideDialog"
import {
  GOODS_RECEIPT_PALLETS_POLL_INTERVAL,
  GOODS_RECEIPT_POLL_INTERVAL,
} from "@/screens/WarehouseReceiving/constants"
import { useGetGoodsReceipt } from "@/screens/WarehouseReceiving/hooks/useGetGoodsReceipt"
import { useGetGoodsReceiptPallets } from "@/screens/WarehouseReceiving/hooks/useGetGoodsReceiptPallets"
import { useGoodsReceiptPalletActions } from "@/screens/WarehouseReceiving/hooks/useGoodsReceiptPalletActions"
import { usePalletSearch } from "@/screens/WarehouseReceiving/hooks/usePalletSearch"
import { GoodsReceiptPalletActionType } from "@/screens/WarehouseReceiving/types"
import {
  getPalletDeleteScenario,
  isAkrochemManagedWarehouse,
  isPalletAs400SyncPending,
} from "@/screens/WarehouseReceiving/utils"

export const BuiltPalletsTab: FC = () => {
  const navigate = useNavigate()
  const { getBasicPathFor } = useAppView()
  const { goodsReceiptId } = useParams({
    from: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/checked-in/$goodsReceiptId/_layout`,
  })
  const {
    pallets,
    isFetching: isPalletsFetching,
    error: palletsError,
    refetch,
  } = useGetGoodsReceiptPallets(goodsReceiptId, {
    isPollingEnabled: true,
    pollInterval: GOODS_RECEIPT_PALLETS_POLL_INTERVAL,
  })
  const isPalletSyncing = pallets.some(isPalletAs400SyncPending)

  const { goodsReceipt, isFetching: isGoodsReceiptFetching } = useGetGoodsReceipt(goodsReceiptId, {
    isPollingEnabled: isPalletSyncing,
    pollInterval: GOODS_RECEIPT_POLL_INTERVAL,
  })
  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  const { updatePallet } = useUpdateGoodsReceiptPallet()

  const {
    actionState,
    openActionsMenuId,
    handleActionsMenuOpen,
    handleActionsMenuClose,
    handleDelete,
    handleDeleteConfirm,
    handleDestinationOverride,
    handleLpnEdit,
    handleQrPrint,
    handleQrPrintAll,
    handleActionSuccess,
    handleActionClose,
  } = useGoodsReceiptPalletActions({
    onRefetch: () => void refetch(),
  })

  const { searchTerm, setSearchTerm, filteredPallets } = usePalletSearch(pallets)

  const {
    rowSelection,
    setRowSelection,
    putAwayModalOpen,
    selectedPallets,
    selectedCount,
    putAwayLoading,
    handlePutAway,
    handleConfirmPutAway,
    handleClosePutAwayModal,
  } = usePutAwayFlow({ goodsReceiptId, filteredPallets, refetch })

  const isAkrochemManaged = isAkrochemManagedWarehouse(goodsReceipt?.warehouseArea?.type)

  useEffect(() => {
    if (isPalletsFetching || isGoodsReceiptFetching) return

    if (!goodsReceipt || !pallets.length) {
      navigate({
        to: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/checked-in`,
      })
      return
    }

    if (goodsReceipt.status === GoodsReceiptStatus.Stored && pallets.every((pallet) => pallet.isStored)) {
      navigate({
        to: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/stored/$goodsReceiptId/built-pallets`,
        params: { goodsReceiptId },
      })
    }
  }, [pallets, goodsReceipt, goodsReceiptId, navigate, getBasicPathFor, isPalletsFetching, isGoodsReceiptFetching])

  const handleDestinationUpdate = useCallback(
    async (pallet: GoodsReceiptPallet, override: DestinationOverride) => {
      await updatePallet({
        goodsReceiptPalletId: pallet.goodsReceiptPalletId,
        destinationWarehouseShelvingId: override.warehouseShelvingId,
      })
      handleActionSuccess()
    },
    [updatePallet, handleActionSuccess]
  )

  if ((isPalletsFetching && pallets.length === 0) || (isGoodsReceiptFetching && !goodsReceipt)) {
    return (
      <Box className='flex h-full items-center justify-center'>
        <CircularProgress />
      </Box>
    )
  }

  if (!goodsReceipt) {
    return null
  }

  if (palletsError) {
    return (
      <Box className='flex h-full flex-col items-center justify-center'>
        <ErrorMessage title='Unable to load pallets' error={palletsError} />
      </Box>
    )
  }

  // Show empty state if no pallets
  if (!pallets.length) {
    return (
      <Box className='flex h-full flex-col items-center justify-center'>
        <InformationMissingAlert title='No pallets have been built for this goods receipt yet.' />
      </Box>
    )
  }

  return (
    <Box className='flex min-h-0 grow flex-col gap-y-4'>
      <Box className='flex items-center gap-4'>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder='Search by Product ID, Lot #, or Reference'
          className='mr-auto max-w-[524px] flex-1'
          data-testid='pallets-search-input'
        />
        <BuiltPalletsActionButtons
          selectedCount={selectedCount}
          goodsReceipt={goodsReceipt}
          pallets={pallets}
          isAkrochemManaged={isAkrochemManaged}
          putAwayLoading={putAwayLoading}
          canMaintain={canMaintain}
          onPutAway={handlePutAway}
          onQrPrintAll={() => handleQrPrintAll(filteredPallets)}
        />
      </Box>

      <CheckedInBuiltPalletsTable
        pallets={filteredPallets}
        warehouse={goodsReceipt.warehouseArea}
        rowSelection={rowSelection}
        isAkrochemManaged={isAkrochemManaged}
        isSyncing={isPalletSyncing}
        isLoading={isPalletsFetching}
        canMaintain={canMaintain}
        openActionsMenuId={openActionsMenuId}
        onActionsMenuOpen={handleActionsMenuOpen}
        onActionsMenuClose={handleActionsMenuClose}
        onDestinationOverride={handleDestinationOverride}
        onLpnEdit={handleLpnEdit}
        onQrPrint={handleQrPrint}
        onDelete={handleDelete}
        onRowSelectionChange={setRowSelection}
      />

      {putAwayModalOpen &&
        (selectedPallets.length === 1 ? (
          <ManualPutAwayModal
            pallet={selectedPallets[0]}
            onClose={handleClosePutAwayModal}
            onConfirm={handleConfirmPutAway}
            loading={putAwayLoading}
          />
        ) : (
          <BulkPutAwayModal
            palletCount={selectedPallets.length}
            onClose={handleClosePutAwayModal}
            onConfirm={handleConfirmPutAway}
            loading={putAwayLoading}
          />
        ))}

      {actionState?.type === GoodsReceiptPalletActionType.EditLpn && (
        <GoodsReceiptPalletLpnFormDialog
          pallet={actionState.pallet}
          onClose={handleActionClose}
          onSubmit={handleActionSuccess}
        />
      )}

      {actionState?.type === GoodsReceiptPalletActionType.Delete && (
        <GoodsReceiptPalletDeleteConfirmation
          scenario={getPalletDeleteScenario(pallets)}
          onClose={handleActionClose}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {actionState?.type === GoodsReceiptPalletActionType.EditDestination && (
        <GoodsReceiptPalletDestinationOverrideDialog
          pallet={actionState.pallet}
          warehouse={goodsReceipt.warehouseArea!}
          pallets={pallets}
          onClose={handleActionClose}
          onSubmit={(override: DestinationOverride) => handleDestinationUpdate(actionState.pallet, override)}
        />
      )}
    </Box>
  )
}
